/**
 * @file MeetingStatusBadge.tsx
 * @brief Visual badge mapping a boardroom meeting `status` → variant.
 *
 * @details
 * Mirrors the colour language of `SessionStatusBadge` so MeetingIntel reads
 * the same as the rest of the panel. The record pipeline moves a meeting
 * through uploading → processing → resolved_pending → synthesis_processing →
 * ready (or error); only `ready` has full intelligence to view.
 *
 * @dependencies
 * - components/ui/Badge
 *
 * @references
 * - features/company-meetings/schemas.ts (MeetingStatus)
 * - features/sessions/components/SessionStatusBadge.tsx (sibling pattern)
 *
 * @author DES
 * @date 08-07-2026
 */

import { Badge } from "@/components/ui/Badge";
import type { MeetingStatus } from "../schemas";
import { failureBadgeLabel, failureReasonOf } from "../meetingFailure";

const LABEL: Record<MeetingStatus, string> = {
  uploading: "Uploading",
  processing: "Transcribing",
  resolved_pending: "Awaiting speakers",
  synthesis_processing: "Compiling",
  ready: "Ready",
  error: "Error",
};

export function MeetingStatusBadge({
  status,
  failureReason,
}: {
  status: MeetingStatus;
  /** "No speech" / "Timed out" for an errored meeting (18-10-2026). */
  failureReason?: string | null;
}) {
  switch (status) {
    case "ready":
      return <Badge variant="success">{LABEL.ready}</Badge>;
    case "error": {
      const reason = failureReasonOf({ status, failure_reason: failureReason });
      return (
        <Badge variant={reason === "no_speech" ? "warning" : "destructive"}>{failureBadgeLabel(reason)}</Badge>
      );
    }
    case "resolved_pending":
      return <Badge variant="warning">{LABEL.resolved_pending}</Badge>;
    case "uploading":
    case "processing":
    case "synthesis_processing":
      return <Badge variant="info">{LABEL[status]}</Badge>;
    default:
      return <Badge variant="muted">{String(status)}</Badge>;
  }
}
